import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  keywords,
  canonical,
  ogType = 'website',
  ogImage = 'https://customer-assets.emergentagent.com/job_harmony-behavioral/artifacts/rov8cr92_HHW_icon.jpg'
}) => {
  const siteTitle = 'Harmony Behavioral & Psychiatric Health';
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  
  const defaultDescription = 'Evidence-informed psychiatric care for ages 5+ across Tennessee, Georgia, Maryland, and DC. Specializing in anxiety, depression, ADHD, trauma, and comprehensive mental health treatment.';
  const metaDescription = description || defaultDescription;

  const defaultKeywords = 'psychiatric nurse practitioner, mental health treatment, psychiatry Tennessee, psychiatry Georgia, psychiatry Maryland, psychiatry DC, anxiety treatment, depression treatment, ADHD treatment, trauma therapy, telepsychiatry, online psychiatrist, medication management, psychiatric evaluation, behavioral health';
  const metaKeywords = keywords || defaultKeywords;

  // Structured Data for LocalBusiness
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": "Harmony Behavioral & Psychiatric Health",
    "alternateName": "Harmony Health & Wellness",
    "description": metaDescription,
    "url": "https://www.harmonybehavioralhealth.com",
    "logo": ogImage,
    "image": ogImage,
    "telephone": "404-738-7542",
    "faxNumber": "678-737-1486",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "US",
      "areaServed": [
        "Tennessee",
        "Georgia", 
        "Maryland",
        "District of Columbia"
      ]
    },
    "areaServed": [
      {
        "@type": "State",
        "name": "Tennessee"
      },
      {
        "@type": "State", 
        "name": "Georgia"
      },
      {
        "@type": "State",
        "name": "Maryland"
      },
      {
        "@type": "Place",
        "name": "District of Columbia"
      }
    ],
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "38.9072",
        "longitude": "-77.0369"
      }
    },
    "medicalSpecialty": [
      "Psychiatry",
      "Behavioral Health",
      "Mental Health"
    ],
    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "Psychiatric Evaluation"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Medication Management"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Anxiety Treatment"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Depression Treatment"
      },
      {
        "@type": "MedicalTherapy",
        "name": "ADHD Treatment"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Trauma Therapy"
      },
      {
        "@type": "MedicalTherapy",
        "name": "Telepsychiatry Services"
      }
    ],
    "paymentAccepted": [
      "Cash",
      "Insurance",
      "Aetna",
      "Anthem Blue Cross Blue Shield",
      "Cigna",
      "UnitedHealthcare",
      "CareFirst"
    ]
  };

  // Provider Structured Data
  const providerData = {
    "@context": "https://schema.org",
    "@type": "Physician",
    "name": "Shaquita Starks",
    "honorificPrefix": "Dr.",
    "honorificSuffix": "PhD, PMHNP-BC, FAAN",
    "jobTitle": "Board-Certified Psychiatric Mental Health Nurse Practitioner",
    "worksFor": {
      "@type": "MedicalBusiness",
      "name": "Harmony Behavioral & Psychiatric Health"
    },
    "medicalSpecialty": "Psychiatry",
    "image": "https://customer-assets.emergentagent.com/job_harmony-behavioral/artifacts/0xv7f47s_starks_shaquita_default_circle_crop_-_street_headshot.jpeg",
    "description": "Dedicated to providing high-quality, evidence-informed care. Specializes in comprehensive psychiatric health for patients across the life-span (ages 5 and up)."
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content="Harmony Behavioral & Psychiatric Health" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical || window.location.href} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical || window.location.href} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Medical/Healthcare Specific */}
      <meta name="medical.specialty" content="Psychiatry" />
      <meta name="medical.practitioner" content="Shaquita Starks, PhD, PMHNP-BC, FAAN" />
      
      {/* Geographic */}
      <meta name="geo.region" content="US-TN" />
      <meta name="geo.region" content="US-GA" />
      <meta name="geo.region" content="US-MD" />
      <meta name="geo.region" content="US-DC" />
      <meta name="geo.placename" content="Tennessee, Georgia, Maryland, District of Columbia" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(providerData)}
      </script>
    </Helmet>
  );
};

export default SEO;
